/**
 * CRE dictation mirror — page side. Protocol: `CREExtension/src/types/mirrorProtocol.ts`
 * and `pageProtocol.ts`. Optional `mirror-state` payload slices: `hooks/cre/index.js`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  CRE_CHANNEL,
  CRE_OPEN_MIRROR_MESSAGE_TYPE,
} from "./cre/creMirrorConstants";

const RPC_TIMEOUT_MS = 15000;
/** Handshake only: resolve quickly so we can show “extension missing” in the UI */
const HANDSHAKE_TIMEOUT_MS = 3200;

function randomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cre-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function sendCreRequest(event, payload, timeoutMs = RPC_TIMEOUT_MS) {
  const requestId = randomId();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("CRE_TIMEOUT"));
    }, timeoutMs);

    function onMessage(e) {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.channel !== CRE_CHANNEL) return;
      if (d.type !== "response") return;
      if (d.requestId !== requestId) return;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      if (d.success) resolve(d.payload);
      else {
        const err = new Error(d.error || "CRE_ERROR");
        err.errorCode = d.errorCode;
        reject(err);
      }
    }

    window.addEventListener("message", onMessage);
    window.postMessage(
      {
        type: "request",
        channel: CRE_CHANNEL,
        event,
        requestId,
        payload,
      },
      window.location.origin,
    );
  });
}

/**
 * Bidirectional mirror: Medilis owns dictation state; CRE extension PiP shows it.
 * Transcript caret ownership is explicit (`transcriptAuthority`: page vs pip) for production-safe sync.
 *
 * @param {object} opts
 * @param {Record<string, string>} [opts.mirrorLabels] PiP + toast strings.
 * @param {Record<string, unknown>} [opts.mirrorPayloadExtension] Spread into `mirror-state` after `labels` (see `hooks/cre/mirrorStateExtensions.js`).
 * @param {() => void | Promise<void | { ok: boolean, mirrorLevel?: string, mirrorMessage?: string }>} [opts.onProcessWithAI]
 *   Invoked when the PiP sends `process.ai`. Return `{ ok, mirrorLevel, mirrorMessage }` so the page can
 *   mirror success/error into the extension via `useCreMirrorFeedback` merged into `mirrorPayloadExtension`.
 * @param {() => void | Promise<void>} [opts.onMacroCommandModeToggle] Invoked when the PiP sends `macro.command.mode.toggle`.
 * @param {(templateId: string) => void} [opts.onTemplateSelect] Invoked when the PiP sends `template.set`.
 * @param {() => void | Promise<void>} [opts.onNewPatient] Invoked when the PiP sends `new.patient`.
 * @param {import("./cre/mirrorPageAuth").CrePageAuth} [opts.pageAuth] Sent on handshake + should be in `mirrorPayloadExtension`.
 */
export function useCreDictationMirror({
  transcriptTextareaRef,
  sidebarTranscript,
  setSidebarTranscript,
  streamingTranscript,
  isRecording,
  recordingTime,
  dictationLanguage,
  setDictationLanguage,
  languages,
  toggleRecording,
  handleDeleteTranscript,
  mirrorLabels,
  mirrorPayloadExtension = {},
  onProcessWithAI,
  onMacroCommandModeToggle,
  onTemplateSelect,
  onNewPatient,
  pageAuth,
}) {
  /** `pending` until handshake finishes — then `present` or `absent`. */
  const [extensionPresence, setExtensionPresence] = useState("pending");
  const [mirrorSessionId, setMirrorSessionId] = useState(null);
  const revRef = useRef(0);
  const mirrorSessionIdRef = useRef(null);
  /** `pip` while the user is editing in the floating transcript; `page` when the main sidebar textarea owns the caret. */
  const transcriptAuthorityRef = useRef("page");
  const schedulePushRef = useRef(() => {});

  const lastPipEditRef = useRef({
    at: 0,
    text: "",
    selStart: 0,
    selEnd: 0,
  });

  useEffect(() => {
    mirrorSessionIdRef.current = mirrorSessionId;
  }, [mirrorSessionId]);

  useEffect(
    () => () => {
      if (mirrorSessionIdRef.current) {
        void sendCreRequest("pip.mirror.close", {}).catch(() => {});
      }
    },
    [],
  );

  const handlersRef = useRef({
    setSidebarTranscript,
    toggleRecording,
    handleDeleteTranscript,
    setDictationLanguage,
    transcriptTextareaRef,
    sidebarTranscript,
    onProcessWithAI,
    onMacroCommandModeToggle,
    onTemplateSelect,
    onNewPatient,
  });

  useEffect(() => {
    handlersRef.current = {
      setSidebarTranscript,
      toggleRecording,
      handleDeleteTranscript,
      setDictationLanguage,
      transcriptTextareaRef,
      sidebarTranscript,
      onProcessWithAI,
      onMacroCommandModeToggle,
      onTemplateSelect,
      onNewPatient,
    };
  }, [
    setSidebarTranscript,
    toggleRecording,
    handleDeleteTranscript,
    setDictationLanguage,
    transcriptTextareaRef,
    sidebarTranscript,
    onProcessWithAI,
    onMacroCommandModeToggle,
    onTemplateSelect,
    onNewPatient,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await sendCreRequest(
          "handshake",
          pageAuth ? { auth: pageAuth } : {},
          HANDSHAKE_TIMEOUT_MS,
        );
        const caps = payload?.capabilities;
        if (cancelled) return;
        if (Array.isArray(caps) && caps.includes("mirror")) {
          setExtensionPresence("present");
        } else {
          setExtensionPresence("absent");
        }
      } catch {
        if (!cancelled) setExtensionPresence("absent");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageAuth]);

  const pushState = useCallback(() => {
    if (!mirrorSessionId) return;
    const el = transcriptTextareaRef?.current;
    const text = sidebarTranscript ?? "";
    const auth = transcriptAuthorityRef.current;
    const pip = lastPipEditRef.current;
    const now = Date.now();

    const usePipSelection =
      auth === "pip" &&
      now - pip.at < 2500 &&
      pip.text === text;

    let selectionStart;
    let selectionEnd;
    if (auth === "page" && el) {
      selectionStart = el.selectionStart;
      selectionEnd = el.selectionEnd;
    } else if (usePipSelection) {
      selectionStart = pip.selStart;
      selectionEnd = pip.selEnd;
    } else if (el) {
      selectionStart = el.selectionStart;
      selectionEnd = el.selectionEnd;
    } else {
      selectionStart = text.length;
      selectionEnd = text.length;
    }

    revRef.current += 1;
    const rtl =
      typeof dictationLanguage === "string" &&
      (dictationLanguage.startsWith("he") || dictationLanguage.startsWith("ar"));

    window.postMessage(
      {
        type: "mirror-state",
        channel: CRE_CHANNEL,
        payload: {
          rev: revRef.current,
          transcriptAuthority: auth,
          postMessageTargetOrigin: window.location.origin,
          transcript: text,
          selectionStart,
          selectionEnd,
          isRecording,
          recordingTimeSec: recordingTime ?? 0,
          streamingTranscript: streamingTranscript ?? "",
          dictationLanguage: dictationLanguage ?? "en-US",
          languages: languages ?? [],
          rtl,
          labels: mirrorLabels,
          ...mirrorPayloadExtension,
        },
      },
      window.location.origin,
    );
  }, [
    mirrorSessionId,
    transcriptTextareaRef,
    sidebarTranscript,
    isRecording,
    recordingTime,
    streamingTranscript,
    dictationLanguage,
    languages,
    mirrorLabels,
    mirrorPayloadExtension,
  ]);

  useEffect(() => {
    schedulePushRef.current = pushState;
  }, [pushState]);

  useEffect(() => {
    function onMsg(e) {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.channel !== CRE_CHANNEL || d.type !== "push") return;
      if (d.event !== "mirror.intent" && d.event !== "mirror.closed") return;

      if (d.event === "mirror.closed") {
        setMirrorSessionId(null);
        transcriptAuthorityRef.current = "page";
        return;
      }

      const intent = d.payload?.intent;
      if (!intent || typeof intent !== "object") return;

      const H = handlersRef.current;

      switch (intent.kind) {
        case "transcript.surface": {
          transcriptAuthorityRef.current = intent.pipTranscriptFocused
            ? "pip"
            : "page";
          queueMicrotask(() => schedulePushRef.current());
          break;
        }
        case "transcript.edit": {
          transcriptAuthorityRef.current = "pip";
          lastPipEditRef.current = {
            at: Date.now(),
            text: intent.text,
            selStart: intent.selectionStart,
            selEnd: intent.selectionEnd,
          };
          H.setSidebarTranscript(intent.text);
          requestAnimationFrame(() => {
            const ta = H.transcriptTextareaRef?.current;
            if (!ta) return;
            const max = ta.value.length;
            const s = Math.max(0, Math.min(intent.selectionStart, max));
            const end = Math.max(0, Math.min(intent.selectionEnd, max));
            try {
              ta.setSelectionRange(s, end);
            } catch {
              /* ignore */
            }
          });
          break;
        }
        case "recording.toggle": {
          void H.toggleRecording();
          break;
        }
        case "transcript.clear": {
          H.handleDeleteTranscript();
          break;
        }
        case "language.set": {
          if (intent.language) H.setDictationLanguage(intent.language);
          break;
        }
        case "copy.transcript": {
          const text = H.sidebarTranscript ?? "";
          if (!text) return;
          void navigator.clipboard.writeText(text).then(
            () => toast.success(mirrorLabels?.contentCopied ?? "Copied"),
            () => toast.error(mirrorLabels?.copyFailed ?? "Copy failed"),
          );
          break;
        }
        case "process.ai": {
          void H.onProcessWithAI?.();
          break;
        }
        case "macro.command.mode.toggle": {
          void H.onMacroCommandModeToggle?.();
          break;
        }
        case "template.set": {
          if (intent.templateId) H.onTemplateSelect?.(intent.templateId);
          break;
        }
        case "new.patient": {
          void H.onNewPatient?.();
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [mirrorLabels]);

  useEffect(() => {
    if (!mirrorSessionId) return;
    pushState();
  }, [
    mirrorSessionId,
    pushState,
    sidebarTranscript,
    streamingTranscript,
    isRecording,
    recordingTime,
    dictationLanguage,
    languages,
    mirrorLabels,
    mirrorPayloadExtension,
  ]);

  useEffect(() => {
    if (!mirrorSessionId) return;
    let timeoutId;
    const schedule = () => {
      if (document.activeElement !== transcriptTextareaRef?.current) return;
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        pushState();
      }, 90);
    };
    document.addEventListener("selectionchange", schedule);
    return () => {
      document.removeEventListener("selectionchange", schedule);
      window.clearTimeout(timeoutId);
    };
  }, [mirrorSessionId, transcriptTextareaRef, pushState]);

  useEffect(() => {
    if (!mirrorSessionId || !isRecording) return;
    const id = window.setInterval(pushState, 1000);
    return () => window.clearInterval(id);
  }, [mirrorSessionId, isRecording, pushState]);

  const notifyMainTranscriptFocus = useCallback(() => {
    transcriptAuthorityRef.current = "page";
    schedulePushRef.current();
  }, []);

  const openMirror = useCallback(async () => {
    try {
      const payload = await sendCreRequest("pip.mirror.open", {});
      const sid = payload?.sessionId;
      if (typeof sid === "string" && sid) {
        transcriptAuthorityRef.current = "page";
        setMirrorSessionId(sid);
      }
    } catch (err) {
      console.error("CRE pip.mirror.open", err);
      toast.error(mirrorLabels?.floatOpenFailed ?? "Could not open floating dictation");
    }
  }, [mirrorLabels?.floatOpenFailed]);

  const closeMirror = useCallback(async () => {
    try {
      await sendCreRequest("pip.mirror.close", {});
    } catch {
      /* ignore */
    }
    transcriptAuthorityRef.current = "page";
    setMirrorSessionId(null);
  }, []);

  const openMirrorRef = useRef(openMirror);
  useEffect(() => {
    openMirrorRef.current = openMirror;
  }, [openMirror]);

  /** Addendum / host embed: parent posts `{ type: CRE_OPEN_MIRROR_MESSAGE_TYPE }` into this iframe. */
  useEffect(() => {
    function onHostOpenMirror(e) {
      if (!e?.data || e.data.type !== CRE_OPEN_MIRROR_MESSAGE_TYPE) return;

      const embedded = window.parent !== window;
      if (embedded) {
        if (e.source !== window.parent) return;
      } else if (e.source !== window) {
        return;
      }

      if (mirrorSessionIdRef.current) return;

      void openMirrorRef.current();
    }

    window.addEventListener("message", onHostOpenMirror);
    return () => window.removeEventListener("message", onHostOpenMirror);
  }, []);

  return {
    extensionPresence,
    extensionPresent: extensionPresence === "present",
    mirrorSessionActive: Boolean(mirrorSessionId),
    openMirror,
    closeMirror,
    notifyMainTranscriptFocus,
    /** Push mirror-state immediately (e.g. after Process AI enables copy). */
    flushMirrorState: pushState,
  };
}
